import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const Privacy: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAuthenticated = !!user;

  return (
    <MobileLayout showNav={isAuthenticated} showHeader={isAuthenticated}>
      <div className="flex-1 flex flex-col">
        {/* Header - Only show when not authenticated */}
        {!isAuthenticated && (
          <div className="px-6 py-4 border-b border-dark-border dark:border-dark-border border-light-border">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
              >
                <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
              </button>
              <h1 className="ml-4 text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
                {t('legal.privacy.title', 'Privacy Policy')}
              </h1>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="prose prose-invert max-w-none">
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t('legal.privacy.lastUpdated', 'Last updated')}:{' '}
              {new Date().toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              1. {t('legal.privacy.informationCollect.title', 'Information We Collect')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.informationCollect.content',
                'We collect information you provide directly to us, such as your email address, username, and conversation data with AI companions.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              2. {t('legal.privacy.howWeUse.title', 'How We Use Your Information')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.howWeUse.content',
                'We use your information to provide and improve our services, process payments, communicate with you, and ensure safety and security.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              3. {t('legal.privacy.dataStorage.title', 'Data Storage')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.dataStorage.content',
                'Your data is stored securely on AWS servers. Conversation history is retained to improve your experience with AI companions.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              4. {t('legal.privacy.dataSharing.title', 'Data Sharing')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.dataSharing.content',
                'We do not sell your personal information. We may share information with service providers who assist us in operating our platform (e.g., Stripe for payments).',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              5. {t('legal.privacy.yourRights.title', 'Your Rights')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.yourRights.content',
                'You have the right to access, update, or delete your personal information. You can clear AI memory or delete your account at any time.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              6. {t('legal.privacy.cookies.title', 'Cookies')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.cookies.content',
                'We use cookies to maintain your session and improve your experience. You can control cookie settings in your browser.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              7. {t('legal.privacy.security.title', 'Security')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.security.content',
                'We implement appropriate security measures to protect your information. However, no method of transmission over the internet is 100% secure.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              8. {t('legal.privacy.smsMessaging.title', 'SMS/Text Messaging')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
              {t(
                'legal.privacy.smsMessaging.content1',
                'When you provide your phone number and consent to receive SMS messages from ChatNexus, we collect and use your mobile phone number to send you conversational text messages related to your AI companion experience. Message frequency varies based on your usage.',
              )}
            </p>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
              {t(
                'legal.privacy.smsMessaging.content2',
                'We do not sell, rent, or share your mobile phone number or any information collected through our SMS program with third parties for their marketing purposes. Your mobile information will not be shared with third parties or affiliates for marketing or promotional purposes.',
              )}
            </p>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
              {t(
                'legal.privacy.smsMessaging.content3',
                'Standard message and data rates may apply. You may opt out of SMS messages at any time by replying STOP to any message. For help, reply HELP or contact us at support@chatnexus.com. Consent to receive SMS is not a condition of purchase or use of our services.',
              )}
            </p>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.privacy.smsMessaging.content4',
                'By opting in to our SMS program, you confirm that you are the owner or authorized user of the mobile device and phone number provided, and that you are authorized to approve the applicable charges.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              9. {t('legal.privacy.contactUs.title', 'Contact Us')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t(
                'legal.privacy.contactUs.content',
                'For privacy concerns, please contact us at privacy@chatnexus.com',
              )}
            </p>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Privacy;
